/**
 * Shared trust / honesty helpers for Operations Dashboard.
 * Policy: never present fabricated operational information as fact.
 * Canonical operational status labels live in @aisecure/status.
 */

export type {
  TrustUnavailableLabel,
  HealthLabel as ServiceHealthLabel,
} from "@aisecure/status";

export {
  trustValue,
  mapHealthLabel,
  mapDoctorLabel,
  LABEL,
} from "@aisecure/status";

export type ServiceStatusTone = "green" | "yellow" | "red" | "unknown";
export type ApiServiceStatus = "green" | "yellow" | "red" | "unknown";

export type ClaimKind = "observation" | "recommendation" | "verified";

export type ConfidenceDisplay = {
  label: "Verified" | "Evidence available" | "Confidence unavailable";
  kind: "verified" | "evidence" | "unavailable";
};

/** Map API service status to Healthy | Degraded | Critical | Unknown. */
export function serviceHealthDisplay(
  status: ApiServiceStatus,
  hasLiveData: boolean,
): { label: import("@aisecure/status").HealthLabel; tone: ServiceStatusTone } {
  if (!hasLiveData || status === "unknown") {
    return { label: "Unknown", tone: "unknown" };
  }
  if (status === "green") return { label: "Healthy", tone: "green" };
  if (status === "yellow") return { label: "Degraded", tone: "yellow" };
  return { label: "Critical", tone: "red" };
}

/** Preserve unknown; never remap unknown → yellow. */
export function mapServiceStatus(status: ApiServiceStatus, hasLiveData: boolean): ServiceStatusTone {
  return serviceHealthDisplay(status, hasLiveData).tone;
}

/**
 * Never label fabricated numeric scores as "AI confidence".
 * Prefer evidence presence or an explicit unavailable state.
 */
export function confidenceDisplay(opts: {
  verified?: boolean;
  reasons?: string[];
}): ConfidenceDisplay {
  if (opts.verified) {
    return { label: "Verified", kind: "verified" };
  }
  if (opts.reasons && opts.reasons.length > 0) {
    return { label: "Evidence available", kind: "evidence" };
  }
  return { label: "Confidence unavailable", kind: "unavailable" };
}

export function claimKindLabel(kind: ClaimKind): string {
  if (kind === "observation") return "Observation";
  if (kind === "recommendation") return "Recommendation";
  return "Verified outcome";
}
